import { useEffect, useRef, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { track } from './lib/analytics';
import './marketing.css';

// Scroll to top on marketing route change (host app has no global handler for these).
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

/**
 * Marketing shell — mounted INSIDE the existing app <BrowserRouter> as a
 * layout route. All marketing styling is scoped beneath `.marketing-root`.
 * No marketing BrowserRouter is introduced.
 */
export default function MarketingLayout() {
  const { pathname } = useLocation();
  const lastTracked = useRef(null);

  // Page-view (+ pricing view) — deduped against React re-renders / StrictMode
  // double-invoke by comparing the last tracked path.
  useEffect(() => {
    if (lastTracked.current === pathname) return;
    lastTracked.current = pathname;
    track('marketing_page_view', { route: pathname });
    if (pathname === '/plans') {
      track('marketing_pricing_viewed', { route: pathname });
    }
  }, [pathname]);

  // Single delegated (capture-phase) click handler — no per-CTA edits, one event
  // per click (no duplicates). Only route + non-PII location id are sent.
  const handleClickCapture = useCallback((e) => {
    const a = e.target.closest && e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const location = a.getAttribute('data-testid') || undefined;
    const route = window.location.pathname;
    if (href.startsWith('mailto:')) {
      track('marketing_email_clicked', { route, location });
    } else if (href === '/login') {
      track('marketing_sign_in_clicked', { route, location });
    } else if (href === '/signup') {
      track('marketing_sign_up_clicked', { route, location });
    } else if (href.includes('cal.com')) {
      track('demo_cta_clicked', { route, location });
    }
  }, []);

  return (
    <div className="marketing-root min-h-screen bg-[#0A0A0F]" onClickCapture={handleClickCapture}>
      <ScrollToTop />
      <Navbar />
      <main><Outlet /></main>
      <Footer />
    </div>
  );
}
